import React, { useEffect, useState } from 'react';
import {
  deleteCompanyNotificationTemplate,
  fetchCompanyNotificationTemplates,
  upsertCompanyNotificationTemplate,
} from '../../services/companyCustomizationApi';
import type { CompanyNotificationTemplate } from '../../services/companyCustomizationApi';
import { safeJsonParse, toPrettyJson } from '../../utils/json';
import { useI18n } from '../../contexts/I18nContext';

interface Props {
  companyId: string;
}

const channels = ['email', 'sms', 'whatsapp', 'push'];

const CompanyNotificationTemplatesManager: React.FC<Props> = ({ companyId }) => {
  const { t } = useI18n();
  const [templates, setTemplates] = useState<CompanyNotificationTemplate[]>([]);
  const [channelFilter, setChannelFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    channel: 'email',
    templateKey: '',
    subject: '',
    body: '',
    dataSchemaText: '',
    isActive: true,
  });
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyNotificationTemplates(companyId, channelFilter || undefined);
      setTemplates(data);
    } catch (err) {
      console.error('[CompanyNotificationTemplatesManager] Failed to load templates', err);
      setError('Não foi possível carregar templates de notificação.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, channelFilter]);

  const resetFeedback = () => {
    setMessage(null);
    setError(null);
  };

  const handleEdit = (template: CompanyNotificationTemplate) => {
    resetFeedback();
    setForm({
      channel: template.channel,
      templateKey: template.templateKey,
      subject: template.subject ?? '',
      body: template.body,
      dataSchemaText: toPrettyJson(template.dataSchema),
      isActive: template.isActive,
    });
  };

  const handleDelete = async (template: CompanyNotificationTemplate) => {
    if (
      !window.confirm(
        `Remover template "${template.templateKey}" do canal "${template.channel}"? Essa ação não pode ser desfeita.`
      )
    ) {
      return;
    }
    resetFeedback();
    setSaving(true);
    try {
      await deleteCompanyNotificationTemplate(companyId, template.channel, template.templateKey);
      await loadTemplates();
      setMessage('Template removido.');
    } catch (err) {
      console.error('[CompanyNotificationTemplatesManager] Failed to delete template', err);
      setError(t('customization.templates.errorRemove'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    resetFeedback();

    if (!form.templateKey.trim() || !form.body.trim()) {
      setError('Informe a chave e o corpo da mensagem.');
      return;
    }

    let dataSchema: Record<string, unknown> = {};
    try {
      dataSchema = form.dataSchemaText.trim()
        ? safeJsonParse(form.dataSchemaText, dataSchema)
        : {};
    } catch {
      setError('JSON do schema inválido.');
      return;
    }

    setSaving(true);
    try {
      await upsertCompanyNotificationTemplate(companyId, {
        channel: form.channel,
        templateKey: form.templateKey.trim(),
        subject: form.subject.trim() || undefined,
        body: form.body.trim(),
        dataSchema,
        isActive: form.isActive,
      });
      await loadTemplates();
      setMessage('Template salvo com sucesso.');
    } catch (err) {
      console.error('[CompanyNotificationTemplatesManager] Failed to save template', err);
      setError('Erro ao salvar template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Templates por canal</h3>
        <select
          value={channelFilter}
          onChange={(event) => setChannelFilter(event.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            minWidth: '180px',
          }}
        >
          <option value="">Todos os canais</option>
          {channels.map((channel) => (
            <option key={channel} value={channel}>
              {channel.toUpperCase()}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={loadTemplates}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            backgroundColor: '#fff',
          }}
          disabled={loading}
        >{t('support.refreshBtn')}</button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
        }}
      >
        {loading ? (
          <div style={{ padding: '16px', color: '#6b7280' }}>{t('notes.loadingTemplates')}</div>
        ) : templates.length === 0 ? (
          <div style={{ padding: '16px', color: '#6b7280' }}>
            Nenhum template para o filtro selecionado.
          </div>
        ) : (
          templates.map((template) => (
            <div
              key={`${template.channel}-${template.templateKey}`}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                padding: '16px',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{template.channel.toUpperCase()}</div>
                  <strong>{template.templateKey}</strong>
                </div>
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: '999px',
                    backgroundColor: template.isActive ? '#dcfce7' : '#fee2e2',
                    color: template.isActive ? '#166534' : '#b91c1c',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {template.isActive ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              {template.subject && (
                <div style={{ fontSize: '13px', color: '#374151' }}>Assunto: {template.subject}</div>
              )}
              <p style={{ fontSize: '13px', color: '#4b5563', margin: 0 }}>{template.body}</p>
              {template.dataSchema && Object.keys(template.dataSchema).length > 0 && (
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#2563eb' }}>
                    Ver schema esperado
                  </summary>
                  <pre
                    style={{
                      fontSize: '12px',
                      backgroundColor: '#f3f4f6',
                      padding: '10px',
                      borderRadius: '6px',
                      marginTop: '8px',
                    }}
                  >
                    {toPrettyJson(template.dataSchema)}
                  </pre>
                </details>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleEdit(template)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >{t('actions.edit')}</button>
                <button
                  type="button"
                  onClick={() => handleDelete(template)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #fecaca',
                    backgroundColor: '#fee2e2',
                    color: '#b91c1c',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >{t('actions.remove')}</button>
              </div>
            </div>
          ))
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          backgroundColor: '#fff',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
        }}
      >
        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 4px 0' }}>Novo template</h3>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>{t('customization.templates.subtitle')}</p>
        </div>

        {(message || error) && (
          <div
            style={{
              gridColumn: '1 / -1',
              padding: '10px 12px',
              borderRadius: '6px',
              border: message ? '1px solid #86efac' : '1px solid #fecaca',
              backgroundColor: message ? '#dcfce7' : '#fee2e2',
              color: message ? '#166534' : '#b91c1c',
            }}
          >
            {message || error}
          </div>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          Canal *
          <select
            value={form.channel}
            onChange={(event) => setForm({ ...form, channel: event.target.value })}
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          >
            {channels.map((channel) => (
              <option key={channel} value={channel}>
                {channel.toUpperCase()}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          Chave *
          <input
            type="text"
            value={form.templateKey}
            onChange={(event) => setForm({ ...form, templateKey: event.target.value })}
            placeholder="ex: welcome_client"
            required
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          Assunto (Email)
          <input
            type="text"
            value={form.subject}
            onChange={(event) => setForm({ ...form, subject: event.target.value })}
            placeholder={t('customization.templates.welcomeSample')}
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
          }}
        >
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
          />
          Template ativo
        </label>

        <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          Corpo da mensagem *
          <textarea
            value={form.body}
            onChange={(event) => setForm({ ...form, body: event.target.value })}
            rows={6}
            placeholder="Olá {{name}}, seu agendamento foi confirmado para {{date}}."
            required
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '14px',
            }}
          />
        </label>

        <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          Schema de dados esperado (JSON)
          <textarea
            value={form.dataSchemaText}
            onChange={(event) => setForm({ ...form, dataSchemaText: event.target.value })}
            rows={4}
            placeholder='{"required_fields": ["name", "date"]}'
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontFamily: 'monospace',
              fontSize: '13px',
            }}
          />
        </label>

        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '10px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {saving ? 'Salvando...' : 'Salvar template'}
          </button>
          <button
            type="button"
            onClick={() =>
              setForm({
                channel: 'email',
                templateKey: '',
                subject: '',
                body: '',
                dataSchemaText: '',
                isActive: true,
              })
            }
            style={{
              padding: '10px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
          >{t('common.clear')}</button>
        </div>
      </form>
    </div>
  );
};

export default CompanyNotificationTemplatesManager;

